import { useEffect, useMemo, useRef, useState } from 'react'
import { Bug, CheckCircle2, FileText, PackagePlus, Search, UploadCloud } from 'lucide-react'
import { StepDebugModal } from './StepDebugModal.tsx'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Input,
  LoadingState,
  Modal,
  useToast,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { updateQuotePosition, type QuotePosition } from '@/api/quotes.ts'

type Mode = 'pick' | 'assign' | 'create'

interface ArticleSearchResult {
  id: string
  itemCode: string
  itemName: string
  manufacturer?: string | null
  salesPrice?: number
  syncStatus?: 'synced' | 'local_only' | 'conflict'
}

interface ArticleGroup {
  id: string
  name: string
}

interface ResolveMissingItemsModalProps {
  open: boolean
  quoteId: string
  positions: QuotePosition[]
  /**
   * Optional explicit list of position IDs that need to be resolved
   * (e.g. because SAP rejected a code as invalid). When not set, all
   * positions without a sapItemCode are listed.
   */
  targetPositionIds?: string[]
  /** List of hint texts (backend validation error messages). */
  hints?: string[]
  /** Tenant of the quote — passed to all article push/create calls. */
  tenantId?: string | null
  onClose: () => void
  onAllResolved: () => void
  onPositionUpdated: (position: QuotePosition) => void
}

// ─── Heuristics: extract ItemCode + Name from a position ─────────────────────

/** Tries to derive a sensible default item code from a position:
 *   1. If position.sapItemCode is set → use it directly
 *   2. Otherwise extract the leading token before " – " / " - " from the
 *      description, provided it looks like an item code
 *      (uppercase letters/digits/hyphens, max. 40 chars).
 */
function resolveItemCode(position: QuotePosition): string {
  if (position.sapItemCode) return position.sapItemCode
  const desc = (position.description ?? '').trim()
  // Separator variants: hyphen-minus (-), en-dash (–), em-dash (—),
  // colon (:) or pipe (|). Some AI responses use only a colon or put
  // the code in parentheses.
  // Examples we cover:
  //   "UCG-MAX – UniFi Cloud Gateway Max"
  //   "STKLISTE - Hardware UniFi Outdoor"
  //   "UDB-PRO—SECTOR – Antenne"
  //   "SKU UCG-MAX UniFi …"
  const m =
    desc.match(/^([A-Z0-9][A-Z0-9._-]{1,40})\s*[–—\-:|]\s+/) ??
    desc.match(/^([A-Z][A-Z0-9._-]{2,40})\s+/) // leading code without separator
  if (m) return m[1] ?? ''
  return ''
}

function resolveItemName(position: QuotePosition): string {
  const desc = (position.description ?? '').trim()
  const code = resolveItemCode(position)
  let stripped = desc
  if (code) {
    const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    stripped = desc.replace(new RegExp(`^${escaped}\\s*[–—\\-:|]?\\s+`), '')
    if (!stripped) stripped = desc
  }
  return truncateItemName(stripped)
}

/** SAP B1 OITM.ItemName is VARCHAR(100). We truncate so that the short
 *  description remains meaningful:
 *   1. first at " · " (middle dot) — marketing text is trimmed away
 *   2. then at " — " / " – " / " - " with context
 *   3. then at word boundary, max. 100 chars
 */
const SAP_ITEM_NAME_MAX = 100
export function truncateItemName(input: string): string {
  const s = (input ?? '').trim()
  if (s.length <= SAP_ITEM_NAME_MAX) return s
  // 1. Split at the first " · "
  const dotIdx = s.indexOf(' · ')
  if (dotIdx > 0 && dotIdx <= SAP_ITEM_NAME_MAX) {
    return s.slice(0, dotIdx).trim()
  }
  // 2. Word boundary starting at character 95
  const slice = s.slice(0, SAP_ITEM_NAME_MAX - 1)
  const lastSpace = slice.lastIndexOf(' ')
  return (lastSpace > 50 ? slice.slice(0, lastSpace) : slice).trim() + '…'
}

// ─── Searchable Select (Combobox) ───────────────────────────────────────────
// Lightweight combobox pattern: input shows the selected label, click/focus
// opens a filtered list, type-to-search. We intentionally avoid HTML <datalist>
// — it has very inconsistent behavior (no display before first input in Safari,
// duplicate lists in Chrome).
function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  allowCustom,
}: {
  value: string
  onValueChange: (v: string) => void
  options: Array<{ value: string; label: string; searchText?: string }>
  placeholder?: string
  disabled?: boolean
  /** Allows free text input as a value (for "manufacturer", "unit", etc.). */
  allowCustom?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = options.find((o) => o.value === value)
  const displayValue = open
    ? query
    : (selected?.label ?? (allowCustom ? value : ''))

  const filtered = options.filter((o) => {
    if (!query) return true
    const haystack = (o.searchText ?? o.label).toLowerCase()
    return haystack.includes(query.toLowerCase())
  })

  // Close on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(allowCustom ? value : '') }}
        onChange={(e) => {
          const v = e.currentTarget.value
          setOpen(true)
          setQuery(v)
          // With allowCustom: adopt input directly as value — the
          // user can enter a custom manufacturer even if it is not
          // in the suggestion list.
          if (allowCustom) onValueChange(v)
        }}
        onBlur={() => {
          if (!allowCustom) return
          // On blur: if the input matches an option label, adopt the option
          // value (so the "canonical" manufacturer is stored instead of the
          // typed variant). Otherwise do nothing — query stays.
          const match = options.find((o) => o.label.toLowerCase() === query.toLowerCase())
          if (match) onValueChange(match.value)
        }}
        disabled={disabled}
        className="w-full rounded-md border border-gray-300 px-3 py-2 pr-8 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => { onValueChange(''); setQuery(''); inputRef.current?.focus() }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-caption"
          aria-label="Auswahl entfernen"
          title="Zurücksetzen"
        >
          ✕
        </button>
      )}
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-caption text-gray-400">Keine Treffer</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onValueChange(o.value); setOpen(false); setQuery('') }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-small hover:bg-brand-50 focus:bg-brand-50 focus:outline-none',
                  o.value === value && 'bg-brand-50 font-medium',
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function ResolveMissingItemsModal({
  open,
  quoteId,
  positions,
  targetPositionIds,
  hints,
  tenantId,
  onClose,
  onAllResolved,
  onPositionUpdated,
}: ResolveMissingItemsModalProps) {
  // Track positions the user has resolved (via assign / create / text line)
  // within this modal session — keyed by position id.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set())

  // Reset resolved set whenever the modal is freshly opened
  useEffect(() => {
    if (open) setResolvedIds(new Set())
  }, [open])

  function markResolved(updated: QuotePosition) {
    setResolvedIds((prev) => {
      const next = new Set(prev)
      next.add(updated.id)
      return next
    })
    onPositionUpdated(updated)
  }

  const affected = useMemo(() => {
    const base = (targetPositionIds && targetPositionIds.length > 0)
      ? (() => {
          const wanted = new Set(targetPositionIds)
          return positions.filter((p) => wanted.has(p.id) && p.kind !== 'note' && p.kind !== 'subtotal')
        })()
      : positions.filter((p) => p.kind !== 'note' && p.kind !== 'subtotal' && !p.sapItemCode)
    return base.filter((p) => !resolvedIds.has(p.id))
  }, [positions, targetPositionIds, resolvedIds])

  // Auto-close when nothing left to resolve
  useEffect(() => {
    if (open && affected.length === 0) onAllResolved()
  }, [open, affected.length, onAllResolved])

  const isInvalidCodeFlow = (targetPositionIds?.length ?? 0) > 0

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={isInvalidCodeFlow ? 'Ungültige SAP-Artikelcodes korrigieren' : 'Positionen ohne SAP-Artikelcode auflösen'}
      description="Für jede betroffene Position kannst du einen bestehenden SAP-Artikel zuweisen, einen neuen anlegen oder die Position als Textzeile übergeben."
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {hints && hints.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-small font-medium text-amber-900 mb-1">SAP hat folgende Probleme gemeldet:</p>
            <ul className="space-y-0.5">
              {hints.map((h, i) => (
                <li key={i} className="text-small text-amber-900">• {h}</li>
              ))}
            </ul>
          </div>
        )}
        <DiagnosticsSection />

        {resolvedIds.size > 0 && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-small text-emerald-900">
              {resolvedIds.size} Position(en) aktualisiert. Beim nächsten „An SAP übergeben" werden die neuen Werte gegen den SAP-Katalog geprüft.
            </p>
          </div>
        )}
        {affected.length === 0 ? (
          <p className="text-body text-gray-600">Alle Positionen wurden aktualisiert. Du kannst das Fenster schließen und den Export erneut starten.</p>
        ) : (
          affected.map((pos) => (
            <PositionRow
              key={pos.id}
              quoteId={quoteId}
              position={pos}
              tenantId={tenantId}
              onResolved={markResolved}
            />
          ))
        )}
      </div>
    </Modal>
  )
}

function PositionRow({
  quoteId,
  position,
  tenantId,
  onResolved,
}: {
  quoteId: string
  position: QuotePosition
  tenantId?: string | null
  onResolved: (p: QuotePosition) => void
}) {
  const [mode, setMode] = useState<Mode>('pick')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localArticle, setLocalArticle] = useState<{ id: string; itemName: string; syncStatus: string } | null>(null)
  const [showStepDebug, setShowStepDebug] = useState(false)
  const { toast } = useToast()

  // If the position already has a code (e.g. "test"), check whether a local
  // article with that code exists. If so and it is local-only, we offer
  // a targeted "Push to SAP again" action.
  useEffect(() => {
    if (!position.sapItemCode) {
      setLocalArticle(null)
      return
    }
    const ctrl = new AbortController()
    apiClient
      .get(`/articles/by-code/${encodeURIComponent(position.sapItemCode)}`, { signal: ctrl.signal })
      .then((r) => setLocalArticle(r.data ?? null))
      .catch(() => setLocalArticle(null))
    return () => ctrl.abort()
  }, [position.sapItemCode])

  async function applyTextLine() {
    setBusy(true)
    setError(null)
    try {
      const updated = await updateQuotePosition(quoteId, position.id, { kind: 'note', sapItemCode: null })
      toast({ title: `„${position.description}" als Textzeile gesetzt`, tone: 'success' })
      onResolved(updated)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      setError(axiosErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen'))
    } finally {
      setBusy(false)
    }
  }

  async function applyAssign(itemCode: string, itemName: string, salesPrice?: number) {
    setBusy(true)
    setError(null)
    try {
      const patch: Record<string, unknown> = { sapItemCode: itemCode }
      if (!position.unitPrice && salesPrice && salesPrice > 0) patch.unitPrice = salesPrice
      const updated = await updateQuotePosition(quoteId, position.id, patch)
      toast({ title: `„${itemName}" zugewiesen`, tone: 'success' })
      onResolved(updated)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      const backendMsg = axiosErr?.response?.data?.error
      setError(backendMsg ?? (err instanceof Error ? err.message : 'Zuweisung fehlgeschlagen'))
    } finally {
      setBusy(false)
    }
  }

  async function pushExistingLocalArticle() {
    if (!position.sapItemCode) return
    setBusy(true)
    setError(null)
    try {
      const pushUrl = tenantId
        ? `/articles/by-code/${encodeURIComponent(position.sapItemCode)}/push?tenantId=${encodeURIComponent(tenantId)}`
        : `/articles/by-code/${encodeURIComponent(position.sapItemCode)}/push`
      const res = await apiClient.post(pushUrl, {})
      const synced = res.data?.syncStatus === 'synced' || (Array.isArray(res.data?.success) && res.data.success.length > 0)
      if (!synced) {
        setError('SAP-Push abgelehnt — Details siehe Backend-Log')
        return
      }
      toast({ title: `„${position.sapItemCode}" wurde an SAP gepusht`, tone: 'success' })
      // Position code is already correct, we just mark it as "resolved"
      onResolved(position)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string; failures?: Array<{ tenantCode: string; error: string }> } } }
      const data = axiosErr?.response?.data
      if (data?.failures && data.failures.length > 0) {
        setError(`SAP-Push abgelehnt: ${data.failures.map((f) => `${f.tenantCode}: ${f.error}`).join('; ')}`)
      } else {
        setError(data?.error ?? (err instanceof Error ? err.message : 'SAP-Push fehlgeschlagen'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-body font-medium text-gray-900 truncate">{position.description}</p>
          <p className="text-caption text-gray-500">
            Menge: {Number(position.quantity)} · {position.kind === 'service' ? 'Dienstleistung' : 'Artikel'}
            {position.unitPrice ? ` · Einzelpreis: ${Number(position.unitPrice).toFixed(2)} €` : ''}
          </p>
          {position.sapItemCode && (
            <p className="text-caption text-red-700 mt-0.5">
              Aktueller Code: <code className="font-mono">{position.sapItemCode}</code> — ungültig
            </p>
          )}
          {localArticle && (
            <p className="text-caption text-amber-800 mt-0.5">
              Lokaler Artikel „{localArticle.itemName}" gefunden — Status:{' '}
              <span className="font-medium">{localArticle.syncStatus === 'synced' ? 'in SAP synchronisiert' : localArticle.syncStatus === 'local_only' ? 'nur lokal (nicht in SAP)' : 'Konflikt'}</span>
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-2 justify-end">
          {localArticle && (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<UploadCloud className="w-4 h-4" />}
              onClick={pushExistingLocalArticle}
              loading={busy}
              disabled={busy}
              title={
                localArticle.syncStatus === 'conflict'
                  ? 'Lokaler Stand wird in SAP geschrieben (Konflikt wird per Overwrite aufgelöst).'
                  : localArticle.syncStatus === 'synced'
                    ? 'Lokal als „synced" markiert, aber SAP findet den Code trotzdem nicht. Re-Push zwingt den Status auf Stand.'
                    : undefined
              }
            >
              {localArticle.syncStatus === 'conflict'
                ? 'Lokalen Stand zu SAP pushen (Konflikt überschreiben)'
                : localArticle.syncStatus === 'synced'
                  ? 'Re-Push erzwingen (Status reparieren)'
                  : 'Lokalen Artikel zu SAP pushen'}
            </Button>
          )}
          {position.sapItemCode && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Bug className="w-4 h-4" />}
              onClick={() => setShowStepDebug(true)}
              title="Push-API-Schritte einzeln ausführen, mit voller SAP-Antwort"
            >
              Step-Debug
            </Button>
          )}
          <Button
            variant={mode === 'assign' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<Search className="w-4 h-4" />}
            onClick={() => setMode((m) => (m === 'assign' ? 'pick' : 'assign'))}
            disabled={busy}
          >
            Zuweisen
          </Button>
          <Button
            variant={mode === 'create' ? 'primary' : 'secondary'}
            size="sm"
            leadingIcon={<PackagePlus className="w-4 h-4" />}
            onClick={() => setMode((m) => (m === 'create' ? 'pick' : 'create'))}
            disabled={busy}
          >
            Neu anlegen
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<FileText className="w-4 h-4" />}
            onClick={applyTextLine}
            disabled={busy}
          >
            Als Textzeile
          </Button>
        </div>
      </div>

      {error && <p className="mt-3 text-small text-red-700">{error}</p>}

      {mode === 'assign' && (
        <AssignArticleForm onPick={applyAssign} disabled={busy} />
      )}

      {mode === 'create' && (
        <CreateArticleForm
          // Key on position ID + extracted code so the form state
          // (incl. itemCode/itemName/groupId) is freshly initialised
          // when switching between positions.
          key={`${position.id}:${resolveItemCode(position)}`}
          defaultItemCode={resolveItemCode(position)}
          defaultName={resolveItemName(position)}
          defaultPrice={position.unitPrice ? Number(position.unitPrice) : undefined}
          tenantId={tenantId}
          onCreated={(itemCode, itemName, salesPrice) => applyAssign(itemCode, itemName, salesPrice)}
          disabled={busy}
        />
      )}

      {position.sapItemCode && (
        <StepDebugModal
          open={showStepDebug}
          itemCode={position.sapItemCode}
          onClose={() => setShowStepDebug(false)}
        />
      )}
    </div>
  )
}

function AssignArticleForm({
  onPick,
  disabled,
}: {
  onPick: (itemCode: string, itemName: string, salesPrice?: number) => void
  disabled: boolean
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ArticleSearchResult[]>([])
  const [includeLocalOnly, setIncludeLocalOnly] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    const ctrl = new AbortController()
    setLoading(true)
    const params: Record<string, unknown> = { q: query, pageSize: 25 }
    // Default: only articles that actually exist in SAP. Local-only articles
    // would otherwise cause a second error during export because SAP does
    // not know them.
    if (!includeLocalOnly) params['syncStatus'] = 'synced'
    apiClient
      .get('/articles', { params, signal: ctrl.signal })
      .then((r) => setResults(r.data?.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [query, includeLocalOnly])

  return (
    <div className="mt-3 space-y-2">
      <Input
        placeholder="SAP-Artikel suchen (Code, Name oder Hersteller) …"
        leadingIcon={<Search className="w-4 h-4" />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        disabled={disabled}
      />
      <label className="flex items-center gap-2 text-caption text-gray-600">
        <input
          type="checkbox"
          checked={includeLocalOnly}
          onChange={(e) => setIncludeLocalOnly(e.currentTarget.checked)}
        />
        Auch nicht-synchronisierte (lokale) Artikel anzeigen
      </label>
      {query.length >= 2 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 max-h-56 overflow-auto">
          {loading ? (
            <p className="px-3 py-2 text-caption text-gray-500">Suche …</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-caption text-gray-500">
              Keine SAP-Artikel gefunden{!includeLocalOnly ? ' (nur synchronisierte werden angezeigt).' : '.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {results.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onPick(a.itemCode, a.itemName, a.salesPrice)}
                    className="flex w-full items-baseline gap-2 px-3 py-2 text-left text-small hover:bg-white disabled:opacity-50"
                  >
                    <span className="font-medium text-gray-900">{a.itemName}</span>
                    <span className="text-caption text-gray-500">{a.itemCode}</span>
                    {a.manufacturer && <span className="text-caption text-gray-400">· {a.manufacturer}</span>}
                    {a.syncStatus && a.syncStatus !== 'synced' && (
                      <span className="ml-auto text-caption text-amber-700">{a.syncStatus === 'local_only' ? 'nur lokal' : 'konflikt'}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function CreateArticleForm({
  defaultItemCode,
  defaultName,
  defaultPrice,
  tenantId,
  onCreated,
  disabled,
}: {
  defaultItemCode?: string
  defaultName: string
  defaultPrice?: number
  tenantId?: string | null
  onCreated: (itemCode: string, itemName: string, salesPrice?: number) => void
  disabled: boolean
}) {
  // AI almost always uses the manufacturer code (e.g. "UCG-MAX") as sapItemCode →
  // use it directly as the default value instead of an invented placeholder.
  const [itemCode, setItemCode] = useState(defaultItemCode ?? '')
  const [itemName, setItemName] = useState(defaultName)
  const [manufacturer, setManufacturer] = useState('')
  const [salesPrice, setSalesPrice] = useState<string>(defaultPrice ? String(defaultPrice) : '')
  const [purchasePrice, setPurchasePrice] = useState<string>('')
  const [unit, setUnit] = useState('Stk')
  const NO_GROUP = '__none__'
  const [groupId, setGroupId] = useState<string>(NO_GROUP)
  const [groups, setGroups] = useState<ArticleGroup[]>([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // If the POST /articles returns a conflict ("exists locally, NOT in SAP"),
  // we store the code + article ID and offer an inline push.
  const [existingLocalOnly, setExistingLocalOnly] = useState<{ id: string; itemCode: string } | null>(null)
  const [pushing, setPushing] = useState(false)

  // SAP master data + central defaults from the article module
  const [sapManufacturers, setSapManufacturers] = useState<string[]>([])
  const [manufacturersError, setManufacturersError] = useState<string | null>(null)
  const [sapUnits, setSapUnits] = useState<string[]>([])
  const [salesMarginPct, setSalesMarginPct] = useState<number>(30)
  const [fetchingEgis, setFetchingEgis] = useState(false)
  const [egisInfo, setEgisInfo] = useState<string | null>(null)

  useEffect(() => {
    // Load article groups + default group + margin + SAP master data +
    // local-DB manufacturers (fallback when SAP has no manufacturer master
    // data) in parallel.
    Promise.all([
      apiClient.get('/articles/groups').then((r) => r.data?.data ?? r.data ?? []).catch(() => []),
      apiClient.get('/articles/defaults').then((r) => r.data).catch(() => ({ salesMarginPct: 30, defaultItemGroupCode: null })),
      apiClient.get('/sap/diagnostics').then((r) => r.data).catch(() => null),
      apiClient.get('/articles/manufacturers').then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
    ]).then(([groupsRes, defaults, diag, localMfs]) => {
      // Source of truth: live list from SAP (`diag.itemGroups`). The local
      // `article_groups` table is intentionally NOT used — in practice it is
      // not cleanly matched to SAP. We build pseudo "ArticleGroup" entries
      // from the SAP data whose `id` is the numeric SAP code; the backend
      // POST /articles accepts either sapGroupCode or groupId.
      const sapItemGroups: Array<{ Number?: number; GroupName?: string }> = diag?.itemGroups ?? []
      const validGroups: ArticleGroup[] = sapItemGroups
        .filter((g) => typeof g.Number === 'number' && typeof g.GroupName === 'string' && g.GroupName.length > 0)
        .map((g) => ({
          id: String(g.Number),
          name: g.GroupName!,
          sapGroupCode: g.Number,
          isActive: true,
        } as unknown as ArticleGroup))
      setGroups(validGroups)
      // Fallback: if SAP returns nothing (diagnostics failed), use the local
      // list so the user at least has something to choose from.
      if (validGroups.length === 0 && Array.isArray(groupsRes) && groupsRes.length > 0) {
        const localFiltered = (groupsRes as ArticleGroup[]).filter((g) => {
          const ext = g as unknown as { sapGroupCode?: number | string | null; isTitle?: boolean; isActive?: boolean }
          return !ext.isTitle && ext.isActive !== false && ext.sapGroupCode != null
        })
        setGroups(localFiltered)
      }
      setSalesMarginPct(Number(defaults?.salesMarginPct ?? 30) || 30)
      // Pre-select article group:
      // 1. Central default group (article module → Defaults) — if set and
      //    present in the filtered group list.
      // 2. Otherwise fall back to the first SAP-valid group.
      let preselected: string | null = null
      if (defaults?.defaultItemGroupCode) {
        const match = validGroups.find(
          (g) => String((g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? '') === String(defaults.defaultItemGroupCode),
        )
        if (match) preselected = match.id
      }
      if (!preselected && validGroups.length > 0) preselected = validGroups[0]!.id
      if (preselected) setGroupId(preselected)
      // Extract SAP manufacturers + UoM
      const mfNames = (diag?.manufacturers ?? [])
        .map((m: { ManufacturerName?: string }) => m.ManufacturerName)
        .filter((n: unknown): n is string => typeof n === 'string' && n.length > 0)
      // Merge SAP manufacturers + local DB manufacturers (Set deduplicates
      // case-sensitively; sorted alphabetically in German locale).
      const allMfsCombined = Array.from(
        new Set<string>([...(mfNames as string[]), ...(localMfs as string[])]),
      ).sort((a, b) => a.localeCompare(b, 'de'))
      setSapManufacturers(allMfsCombined)
      // If SAP manufacturers could not be loaded, make the error visible in
      // the UI (CLAUDE.md rule "no silent catches").
      if (diag?.manufacturersError) {
        setManufacturersError(String(diag.manufacturersError))
      }
      // Guess manufacturer from the description and pre-fill — independent of
      // EGIS so the field doesn't stay empty when EGIS is unreachable. We match
      // the description text against the list of known SAP manufacturers
      // (substring, case-insensitive). Additionally a few common brand aliases
      // (the SAP manufacturer name sometimes differs from the product brand in
      // the description).
      const haystack = `${defaultName ?? ''} ${defaultItemCode ?? ''}`.toLowerCase()
      const aliasMap: Record<string, string[]> = {
        Ubiquiti: ['unifi', 'ubiquiti', 'ubnt'],
        Lenovo: ['lenovo', 'thinkpad', 'thinkcentre', 'thinkbook'],
        HP: ['hewlett-packard', 'hewlett packard', 'probook', 'elitebook', 'hp '],
        Dell: ['dell', 'latitude', 'optiplex', 'precision'],
        Yealink: ['yealink'],
        Microsoft: ['microsoft', 'surface', 'teams'],
        Cisco: ['cisco', 'meraki'],
        Fortinet: ['fortinet', 'fortigate'],
        Eaton: ['eaton'],
        APC: ['apc'],
      }
      const allMfs = allMfsCombined
      function findManufacturer(): string | null {
        for (const mf of allMfs) {
          if (haystack.includes(mf.toLowerCase())) return mf
        }
        for (const [canonical, aliases] of Object.entries(aliasMap)) {
          if (aliases.some((a) => haystack.includes(a))) {
            const sapMatch = allMfs.find((m) => m.toLowerCase() === canonical.toLowerCase())
            return sapMatch ?? canonical
          }
        }
        return null
      }
      const guessed = findManufacturer()
      if (guessed) setManufacturer((prev) => (prev === '' ? guessed : prev))
      // UoM groups provide codes (e.g. "Stk", "Std", "m") — we use the
      // code/name as suggestion.
      const uomNames = (diag?.uomGroups ?? [])
        .map((u: { Code?: string; Name?: string }) => u.Code ?? u.Name)
        .filter((n: unknown): n is string => typeof n === 'string' && n.length > 0)
      setSapUnits(Array.from(new Set(['Stk', 'Std', ...(uomNames as string[])])).sort())
    }).finally(() => setLoadingGroups(false))
  }, [])

  async function fetchEkFromEgis() {
    if (!itemCode.trim()) {
      setError('Bitte zuerst den SAP-Artikelcode eingeben — er wird als EGIS-Suchbegriff genutzt.')
      return
    }
    setFetchingEgis(true)
    setEgisInfo(null)
    setError(null)
    try {
      const res = await apiClient.get('/egis/search', { params: { q: itemCode.trim() } })
      const items: Array<{
        productNumber?: string
        description?: string
        manufacturer?: string
        purchasePrice?: number | null
        recommendedRetailPrice?: number | null
      }> = res.data?.items ?? res.data?.data?.items ?? res.data ?? []
      if (!items.length) {
        setEgisInfo(`Keine EGIS-Treffer für „${itemCode}". Bitte EK manuell eintragen.`)
        return
      }
      const hit = items[0]
      if (typeof hit.purchasePrice === 'number') {
        setPurchasePrice(hit.purchasePrice.toFixed(2))
        // Calculate the sales price at the same time — default 30%, overridable.
        if (!salesPrice) {
          const vk = hit.purchasePrice * (1 + salesMarginPct / 100)
          setSalesPrice(vk.toFixed(2))
        }
      }
      if (!manufacturer && hit.manufacturer) setManufacturer(hit.manufacturer)
      setEgisInfo(`EGIS-Treffer: ${hit.description ?? hit.productNumber} (EK: ${hit.purchasePrice?.toFixed(2) ?? '—'} €)`)
    } catch (err) {
      // On automatic call, don't show the full error banner —
      // just a subtle hint in the EGIS info field. The manufacturer may
      // already have been guessed from the description.
      const status = (err as { response?: { status?: number } })?.response?.status
      const detail = status === 502
        ? 'EGIS-Gateway aktuell nicht erreichbar (502).'
        : err instanceof Error ? err.message : 'unbekannter Fehler'
      setEgisInfo(`EGIS-Abruf fehlgeschlagen: ${detail}`)
    } finally {
      setFetchingEgis(false)
    }
  }

  function calculateVkFromEk() {
    const ek = parseFloat(purchasePrice)
    if (!isFinite(ek) || ek <= 0) {
      setError('Bitte zuerst einen EK-Preis eingeben (oder „EK aus EGIS" nutzen).')
      return
    }
    const vk = ek * (1 + salesMarginPct / 100)
    setSalesPrice(vk.toFixed(2))
    setError(null)
  }

  // If defaultItemCode changes after mount (e.g. because the position arrives
  // after the first render, or because the modal has switched between
  // positions), keep the itemCode state in sync.
  // Only when the user has not typed anything yet — we do not overwrite manual input.
  useEffect(() => {
    if (!defaultItemCode) return
    setItemCode((prev) => (prev === '' ? defaultItemCode : prev))
  }, [defaultItemCode])
  useEffect(() => {
    if (defaultName) setItemName((prev) => (prev === '' ? defaultName : prev))
  }, [defaultName])

  // On first open with a pre-filled ItemCode: run the EGIS lookup once
  // automatically so that manufacturer + purchase price are pre-filled and
  // the user only needs to click "Calculate" → "Create article".
  const [autoEgisDone, setAutoEgisDone] = useState(false)
  useEffect(() => {
    if (autoEgisDone) return
    if (!defaultItemCode) return
    if (loadingGroups) return // wait until diagnostics have loaded
    setAutoEgisDone(true)
    void fetchEkFromEgis()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItemCode, loadingGroups, autoEgisDone])

  async function pushExistingToSap() {
    if (!existingLocalOnly) return
    setPushing(true)
    setError(null)
    try {
      // 1. Overwrite the local article with the current form values — the
      //    existing entry would otherwise still have the old values (e.g. wrong
      //    article group) that caused the previous push to fail.
      const patchBody: Record<string, unknown> = {
        itemName: itemName.trim(),
        unit,
      }
      if (manufacturer.trim()) patchBody['manufacturer'] = manufacturer.trim()
      if (salesPrice) patchBody['salesPrice'] = Number(salesPrice)
      if (purchasePrice) patchBody['purchasePrice'] = Number(purchasePrice)
      if (groupId && groupId !== NO_GROUP) {
        const picked = groups.find((g) => g.id === groupId)
        const sap = (picked as unknown as { sapGroupCode?: number | string | null })?.sapGroupCode
        if (sap != null) patchBody['sapGroupCode'] = sap
        else patchBody['groupId'] = groupId
      }
      await apiClient.patch(`/articles/${existingLocalOnly.id}`, patchBody)
      // 2. Explicit push that returns the SAP error cleanly (PATCH
      //    swallowed errors).
      const existingPushUrl = tenantId
        ? `/articles/by-code/${encodeURIComponent(existingLocalOnly.itemCode)}/push?tenantId=${encodeURIComponent(tenantId)}`
        : `/articles/by-code/${encodeURIComponent(existingLocalOnly.itemCode)}/push`
      const res = await apiClient.post(existingPushUrl, {})
      onCreated(res.data?.itemCode ?? existingLocalOnly.itemCode, itemName.trim(), salesPrice ? Number(salesPrice) : undefined)
      setExistingLocalOnly(null)
    } catch (err) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
      setError(axiosErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Push fehlgeschlagen'))
    } finally {
      setPushing(false)
    }
  }

  async function submit() {
    setError(null)
    if (!itemCode.trim()) return setError('SAP-Artikelcode ist Pflicht')
    if (!itemName.trim()) return setError('Artikelname ist Pflicht')
    if (itemName.trim().length > SAP_ITEM_NAME_MAX) {
      return setError(`Artikelname ist zu lang (max. ${SAP_ITEM_NAME_MAX} Zeichen). SAP würde den POST sonst mit „Value too long in property 'ItemName'" abweisen.`)
    }
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        itemCode: itemCode.trim(),
        itemName: itemName.trim(),
        unit,
      }
      if (manufacturer.trim()) body.manufacturer = manufacturer.trim()
      if (salesPrice) body.salesPrice = Number(salesPrice)
      if (purchasePrice) body.purchasePrice = Number(purchasePrice)
      if (groupId && groupId !== NO_GROUP) {
        const picked = groups.find((g) => g.id === groupId)
        const sap = (picked as unknown as { sapGroupCode?: number | string | null })?.sapGroupCode
        if (sap != null) body.sapGroupCode = sap
        else body.groupId = groupId
      }
      const articlesUrl = tenantId ? `/articles?tenantId=${encodeURIComponent(tenantId)}` : '/articles'
      const res = await apiClient.post(articlesUrl, body)
      const created = res.data
      // If the article was not pushed to any SAP tenant, we must NOT assign
      // the position — otherwise the export will hit the same error again.
      const synced =
        created?.syncStatus === 'synced' ||
        (Array.isArray(created?.pushedTenants) && created.pushedTenants.length > 0)
      if (!synced) {
        const failed = Array.isArray(created?.pushFailedTenants) && created.pushFailedTenants.length > 0
          ? ` (Tenants: ${created.pushFailedTenants.join(', ')})`
          : ''
        setError(
          `Artikel wurde lokal gespeichert, aber NICHT in SAP angelegt${failed}. ` +
          `Bitte SAP-Pflichtfelder prüfen (Item-Code-Format/Warengruppe (SAP)) und es erneut versuchen, oder einen anderen Artikel zuweisen.`,
        )
        return
      }
      onCreated(created.itemCode, created.itemName, created.salesPrice)
    } catch (err) {
      const axiosErr = err as {
        response?: { status?: number; data?: { error?: string; code?: string; existing?: { id?: string; syncStatus?: string } } }
      }
      const data = axiosErr?.response?.data
      // 502 SAP_PUSH_FAILED: article was created locally but SAP rejected
      // it. Pass the original error message to the user.
      if (axiosErr?.response?.status === 502) {
        setError(data?.error ?? 'SAP hat den Artikel abgelehnt')
        return
      }
      // 409 ARTICLE_EXISTS: article already exists locally. If it is
      // local_only, offer an inline push directly — otherwise the user
      // would unnecessarily have to switch to article management.
      if (axiosErr?.response?.status === 409 && data?.existing?.syncStatus === 'local_only') {
        const existing = data.existing as { id?: string }
        if (existing?.id) {
          setExistingLocalOnly({ id: existing.id, itemCode: itemCode.trim() })
          setError(null)
        } else {
          setError(
            `Artikel mit Code "${itemCode.trim()}" existiert lokal, ist aber NICHT in SAP. ` +
            `Bitte einen anderen ItemCode wählen oder den bestehenden Artikel zuerst über die Artikelverwaltung an SAP pushen.`,
          )
        }
        return
      }
      setError(data?.error ?? (err instanceof Error ? err.message : 'Anlage fehlgeschlagen'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-3 space-y-3">
      {loadingGroups ? (
        <LoadingState label="Warengruppen (SAP) werden geladen…" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="SAP-Artikelcode *"
              placeholder="z. B. SVC-TEAMS-INST"
              value={itemCode}
              onChange={(e) => setItemCode(e.currentTarget.value)}
              disabled={disabled || submitting}
            />
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                Artikelname *
                <span className={cn(
                  'ml-1 text-caption',
                  itemName.length > SAP_ITEM_NAME_MAX ? 'text-red-600' : 'text-gray-400',
                )}>
                  ({itemName.length}/{SAP_ITEM_NAME_MAX})
                </span>
              </label>
              <input
                value={itemName}
                onChange={(e) => setItemName(e.currentTarget.value)}
                disabled={disabled || submitting}
                maxLength={SAP_ITEM_NAME_MAX}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                Hersteller
                {sapManufacturers.length > 0 && (
                  <span className="ml-1 text-caption text-gray-400">({sapManufacturers.length} Vorschläge)</span>
                )}
                {manufacturersError && (
                  <span className="ml-1 text-caption text-red-600" title={manufacturersError}>
                    · SAP-Hersteller-Abruf fehlgeschlagen
                  </span>
                )}
              </label>
              <SearchableSelect
                value={manufacturer}
                onValueChange={setManufacturer}
                placeholder="auswählen oder neu eingeben — tippen zum Filtern"
                disabled={disabled || submitting}
                allowCustom
                options={sapManufacturers.map((m) => ({ value: m, label: m }))}
              />
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">Einheit</label>
              <SearchableSelect
                value={unit}
                onValueChange={setUnit}
                placeholder="z.B. Stk — tippen zum Filtern"
                disabled={disabled || submitting}
                allowCustom
                options={sapUnits.map((u) => ({ value: u, label: u }))}
              />
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                EK-Preis (€)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.currentTarget.value)}
                  disabled={disabled || submitting}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={fetchEkFromEgis}
                  loading={fetchingEgis}
                  disabled={disabled || submitting || fetchingEgis}
                  title="Einkaufspreis vom Distributor (EGIS) abrufen"
                >
                  Aus EGIS
                </Button>
              </div>
              {egisInfo && <p className="text-caption text-gray-500 mt-1">{egisInfo}</p>}
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                VK-Preis (€)
                <span className="ml-1 text-caption text-gray-400">Marge {salesMarginPct}%</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50"
                  value={salesPrice}
                  onChange={(e) => setSalesPrice(e.currentTarget.value)}
                  disabled={disabled || submitting}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={calculateVkFromEk}
                  disabled={disabled || submitting}
                  title={`VK = EK × (1 + ${salesMarginPct}%)`}
                >
                  Kalkulieren
                </Button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-small font-medium text-gray-700 mb-1 block">
                Warengruppe (SAP)
                <span className="ml-1 text-caption text-gray-400">
                  ({groups.length} verfügbar — tippen zum Filtern)
                </span>
              </label>
              <SearchableSelect
                value={groupId === NO_GROUP ? '' : groupId}
                onValueChange={(v) => setGroupId(v || NO_GROUP)}
                placeholder={'(keine) — zum Suchen tippen, z.B. „Hardware"…'}
                disabled={disabled || submitting}
                options={groups.map((g) => {
                  const code = (g as unknown as { sapGroupCode?: number | string }).sapGroupCode
                  return {
                    value: g.id,
                    label: code != null ? `${code} – ${g.name}` : g.name,
                    searchText: `${code ?? ''} ${g.name}`,
                  }
                })}
              />
            </div>
          </div>
          {existingLocalOnly && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
              <p className="text-small text-amber-900">
                Artikel mit Code <span className="font-mono">{existingLocalOnly.itemCode}</span> existiert
                bereits lokal, ist aber noch nicht in SAP angelegt (vermutlich ein Rest eines
                früheren fehlgeschlagenen Pushs). Beim Klick werden die obigen Form-Werte
                (Warengruppe (SAP), Hersteller, Preise) übernommen und der Artikel an SAP übergeben.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<UploadCloud className="w-4 h-4" />}
                  onClick={pushExistingToSap}
                  loading={pushing}
                  disabled={disabled || pushing}
                >
                  Bestehenden Artikel an SAP pushen
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExistingLocalOnly(null)}
                  disabled={pushing}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
          {error && <p className="text-small text-red-700">{error}</p>}
          {!existingLocalOnly && (
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<CheckCircle2 className="w-4 h-4" />}
                onClick={submit}
                loading={submitting}
                disabled={disabled || submitting}
              >
                Artikel anlegen & zuweisen
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Diagnostics Section ─────────────────────────────────────────────────────
// Shows the active tenant, the stored default PriceList value and the
// PriceLists delivered directly from SAP. Helps understand why a particular
// PriceList index ends up in the push request.

interface DiagnosticsResponse {
  tenant?: { id: string; code?: string; name?: string }
  sapAvailable?: boolean
  settings?: Record<string, string | null>
  priceLists?: Array<{ PriceListNo: number; PriceListName?: string; Active?: 'tYES' | 'tNO' }>
  priceListsError?: string | null
  manufacturers?: Array<{ Code: number; ManufacturerName?: string; Status?: 'msActive' | 'msInactive' }>
  manufacturersError?: string | null
  uomGroups?: Array<{ AbsEntry: number; Code?: string; Name?: string }>
  uomGroupsError?: string | null
  itemGroups?: Array<{ Number: number; GroupName?: string }>
  itemGroupsError?: string | null
  itemTemplate?: Record<string, unknown> | null
  itemTemplateError?: string | null
}

function DiagnosticsSection() {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<DiagnosticsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get('/sap/diagnostics')
      setData(res.data)
    } catch (err) {
      const axErr = err as { response?: { data?: { error?: string } } }
      setError(axErr?.response?.data?.error ?? (err instanceof Error ? err.message : 'Diagnose fehlgeschlagen'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && !data && !loading) load()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <details
      className="rounded-md border border-gray-200 bg-gray-50"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer px-3 py-2 text-small font-medium text-gray-700 select-none">
        Diagnose: aktiver Mandant, gespeicherte SAP-Settings, verfügbare PriceLists
      </summary>
      <div className="border-t border-gray-200 p-3 space-y-3 text-caption">
        {loading && <p className="text-gray-500">Lade…</p>}
        {error && <p className="text-red-700">{error}</p>}
        {data && (
          <>
            <div>
              <p className="font-medium text-gray-700">Aktiver Mandant</p>
              <p className="text-gray-600 font-mono">
                {data.tenant?.code ?? '?'} ({data.tenant?.id ?? 'ohne ID'})
                {data.tenant?.name ? ` — ${data.tenant.name}` : ''}
              </p>
              <p className="text-gray-600">
                SAP-Adapter konfiguriert: <span className="font-medium">{data.sapAvailable ? 'ja' : 'nein'}</span>
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">SysMan-Settings</p>
              <ul className="mt-0.5 space-y-0.5 text-gray-600">
                {Object.entries(data.settings ?? {}).map(([k, v]) => (
                  <li key={k} className="font-mono">
                    {k} = {v === null || v === undefined || v === '' ? <span className="text-gray-400">(nicht gesetzt)</span> : <span className="text-gray-900">{String(v)}</span>}
                  </li>
                ))}
              </ul>
              <p className="text-gray-500 mt-1">
                Wenn <code className="font-mono">sap.defaultPriceListNo</code> gesetzt ist, schreibt SysMan den Verkaufspreis in diese Liste. Wert anpassen unter Einstellungen → Module → Verkauf → Aufträge & Angebote.
              </p>
              <p className="text-gray-500 mt-1">
                Erscheint trotz korrekter Nummer „Base price list not found", liegt das in SAP B1: eine andere Preisliste verweist via <code className="font-mono">BasePriceList</code> auf eine gelöschte/inaktive Liste, und SAP rechnet die Preis-Kette für ALLE Listen durch. SysMan legt das Item deshalb erst ohne <code className="font-mono">ItemPrices</code> an und patcht den Preis nachträglich nur auf die gewählte Liste. Wenn auch der Patch fehlschlägt, ist das Item trotzdem in SAP und kann manuell bepreist werden.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">SAP-PriceLists (live aus SAP)</p>
              {data.priceListsError ? (
                <p className="text-red-700">{data.priceListsError}</p>
              ) : (data.priceLists?.length ?? 0) === 0 ? (
                <p className="text-gray-500">— keine —</p>
              ) : (
                <ul className="mt-0.5 space-y-0.5 text-gray-600">
                  {data.priceLists!.map((pl) => (
                    <li key={pl.PriceListNo} className="font-mono">
                      {pl.PriceListNo} — {pl.PriceListName ?? '(ohne Name)'} {pl.Active === 'tNO' ? <span className="text-red-600">[inaktiv]</span> : <span className="text-emerald-700">[aktiv]</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-700">SAP-Hersteller (live aus SAP)</p>
              {data.manufacturersError ? (
                <p className="text-red-700">{data.manufacturersError}</p>
              ) : (data.manufacturers?.length ?? 0) === 0 ? (
                <>
                  <p className="text-gray-500">— keine —</p>
                  <p className="text-gray-500 mt-1">
                    Manufacturer ist in SAP B1 optional. SysMan lässt das Feld dann komplett weg
                    und SAP nutzt seinen internen Default. Falls du gezielt einen Hersteller
                    setzen willst, in den Einstellungen → Module → Verkauf → Aufträge & Angebote
                    den Default-Hersteller wählen (Liste ist hier leer, weil dein SAP keine
                    Hersteller-Stammdaten hat).
                  </p>
                </>
              ) : (
                <ul className="mt-0.5 space-y-0.5 text-gray-600 max-h-32 overflow-y-auto">
                  {data.manufacturers!.slice(0, 25).map((m) => (
                    <li key={m.Code} className="font-mono">
                      {m.Code} — {m.ManufacturerName ?? '(ohne Name)'} {m.Status === 'msInactive' ? <span className="text-red-600">[inaktiv]</span> : <span className="text-emerald-700">[aktiv]</span>}
                    </li>
                  ))}
                  {(data.manufacturers!.length > 25) && (
                    <li className="text-gray-400">… und {data.manufacturers!.length - 25} weitere</li>
                  )}
                </ul>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-700">SAP-Mengeneinheiten-Gruppen (UoMGroups)</p>
              {data.uomGroupsError ? (
                <p className="text-red-700">{data.uomGroupsError}</p>
              ) : (data.uomGroups?.length ?? 0) === 0 ? (
                <p className="text-gray-500">— keine —</p>
              ) : (
                <ul className="mt-0.5 space-y-0.5 text-gray-600 max-h-32 overflow-y-auto">
                  {data.uomGroups!.slice(0, 25).map((g) => (
                    <li key={g.AbsEntry} className="font-mono">
                      {g.AbsEntry} — {g.Code ? `[${g.Code}] ` : ''}{g.Name ?? '(ohne Name)'}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-gray-500 mt-1">
                Wird beim Item-Create als Pflichtfeld <code className="font-mono">UoMGroupEntry</code>
                gesetzt. Default überschreibbar mit <code className="font-mono">sap.defaultUoMGroupEntry</code>.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Warengruppen (SAP) (ItemGroups)</p>
              {data.itemGroupsError ? (
                <p className="text-red-700">{data.itemGroupsError}</p>
              ) : (data.itemGroups?.length ?? 0) === 0 ? (
                <p className="text-gray-500">— keine —</p>
              ) : (
                <ul className="mt-0.5 space-y-0.5 text-gray-600 max-h-32 overflow-y-auto">
                  {data.itemGroups!.slice(0, 25).map((g) => (
                    <li key={g.Number} className="font-mono">
                      {g.Number} — {g.GroupName ?? '(ohne Name)'}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-gray-500 mt-1">
                Wird beim Item-Create als Pflichtfeld <code className="font-mono">ItemsGroupCode</code>
                gesetzt. Default überschreibbar mit <code className="font-mono">sap.defaultItemsGroupCode</code>.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700">Item-Template (existierendes SAP-Item als Vorlage)</p>
              {data.itemTemplateError ? (
                <p className="text-red-700">{data.itemTemplateError}</p>
              ) : data.itemTemplate === null || data.itemTemplate === undefined ? (
                <p className="text-gray-500">— keine Vorlage gefunden —</p>
              ) : (
                <details className="mt-1">
                  <summary className="cursor-pointer text-gray-600">JSON anzeigen ({Object.keys(data.itemTemplate).length} Felder)</summary>
                  <pre className="mt-1 p-2 rounded bg-white border border-gray-200 max-h-48 overflow-auto text-caption font-mono whitespace-pre">
                    {JSON.stringify(data.itemTemplate, null, 2)}
                  </pre>
                </details>
              )}
              <p className="text-gray-500 mt-1">
                SysMan kopiert die Felder dieses bestehenden Items (Schlüssel- und Transaktionsfelder
                werden gestrippt) und überschreibt nur ItemCode/ItemName/Preis. Damit treffen wir
                exakt die Pflichtfeld-Konfiguration deines SAP-Mandanten — auch UDF-getriggerte.
              </p>
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" size="xs" onClick={load} loading={loading}>
                Aktualisieren
              </Button>
            </div>
          </>
        )}
      </div>
    </details>
  )
}

